import React, { useState } from 'react';
import { Text, Stack, Box, Button, Modal } from '@mantine/core';
import { MENTORSHIP_GOALS } from '@/constants/mentorshipGoals';
import { MENTORSHIP_TIMELINES } from '@/constants/mentorshipTimelines';
import { RequestQuestion } from '@/types/mentorship';

interface Props {
  answersJson: RequestQuestion[];
  context?: 'chat' | 'profile';
}

const MentorshipApplicationBasicInfo = ({ answersJson, context = 'profile' }: Props) => {
  // Hooks must be called at the top, before any conditional returns
  const [modalOpened, setModalOpened] = useState(false);
  
  if (!answersJson) {
    return <></>;
  }

  const question = (id: string) => {
    return answersJson.filter((v: any) => v.question == id)[0]?.answer;
  }

  const optionValue = (options: any, option: any) => {
    return options.filter((v: any) => v.value == option)[0]?.label;
  }

  const isChat = context === 'chat';

  if (isChat) {
    // Modern, compact styling for chat context
    return (
      <Stack gap="md">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>
          Mentorship Application
        </Text>
        
        <Box>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.5px' }}>
            What is my goal for Mentorship?
          </Text>
          <Text size="sm" style={{ lineHeight: 1.5 }}>
            {optionValue(MENTORSHIP_GOALS, question('mentorshipGoal'))}
          </Text>
        </Box>

        <Box>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.5px' }}>
            What is my timeline for achieving this goal?
          </Text>
          <Text size="sm" style={{ lineHeight: 1.5 }}>
            {optionValue(MENTORSHIP_TIMELINES, question('mentorshipTimeline'))}
          </Text>
        </Box>

        <Box>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.5px' }}>
            Additional message
          </Text>
          <Text size="sm" style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {question('message')}
          </Text>
        </Box>
      </Stack>
    );
  }

  // Compact styling for profile/review context
  const fullMessage = question('message') || '';
  
  // Truncate to first 100 characters or first 2 sentences, whichever is shorter
  const truncateMessage = (text: string) => {
    if (!text) return '';
    
    // Find first 2 sentences (split by . ! ?)
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
    const firstTwoSentences = sentences.slice(0, 2).join(' ');
    
    // Limit to 100 characters
    const truncated = text.length > 100 ? text.substring(0, 100) : firstTwoSentences;
    
    return truncated;
  };
  
  const truncatedMessage = truncateMessage(fullMessage);
  const shouldTruncate = fullMessage.length > 100;
  
  const goalLabel = optionValue(MENTORSHIP_GOALS, question('mentorshipGoal'));
  const timelineLabel = optionValue(MENTORSHIP_TIMELINES, question('mentorshipTimeline'));

  // Remove trailing period from goal if present
  const goalText = goalLabel?.replace(/\.$/, '') || '';
  const timelineText = timelineLabel?.replace(/\.$/, '') || '';
  
  // Check if it's a special case (prefer not to share)
  const isPrivateGoal = goalLabel?.toLowerCase().includes('prefer not to share');

  return (
    <>
      <Stack gap="sm">
        <Box>
          {isPrivateGoal ? (
            <Text size="sm" c="dark" ta="center" style={{ lineHeight: 1.6 }}>
              {goalLabel}
            </Text>
          ) : (
            <Text size="sm" c="dark" ta="center" style={{ lineHeight: 1.6 }}>
              I need <strong>{goalText.toLowerCase()}</strong> {timelineText.toLowerCase()}.
            </Text>
          )}
        </Box>

        <Box>
          <Text size="sm" c="dark" ta="center" style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {shouldTruncate ? truncatedMessage + '...' : fullMessage}
          </Text>
          {shouldTruncate && (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setModalOpened(true)}
              mt="xs"
              px={0}
              style={{ display: 'block', margin: '8px auto 0' }}
            >
              See more
            </Button>
          )}
        </Box>
      </Stack>
      
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Additional Message"
        size="md"
        centered
      >
        <Text size="sm" style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {fullMessage}
        </Text>
      </Modal>
    </>
  );
};

export default MentorshipApplicationBasicInfo;